import dayjs from "./dayjsConfig";
import {
  deleteRefreshTokenSecure,
  getRefreshTokenSecure,
  saveRefreshTokenSecure,
} from "./biometric";
import { BIOMETRIC_REFRESH_TTL_SECONDS } from "../constants";

export type BiometricCredential = Awaited<
  ReturnType<typeof getRefreshTokenSecure>
>;

export type ValidBiometricCredential = BiometricCredential & {
  refreshToken: string;
  serverDiffMs: number;
  refreshExpiresAt: string;
  state?: string | null;
};

/**
 * Load the stored biometric credential from secure storage.
 * The optional `source` is forwarded to logs so QA can identify the
 * caller (bootstrap, resume, quick-login, etc.).
 */
export const fetchStoredBiometricCredential = async (
  source = "unknown",
): Promise<BiometricCredential | undefined> => {
  return getRefreshTokenSecure(source);
};

/**
 * Validate the stored credential structure and expiry against server diff.
 */
export const validateBiometricCredential = (
  credential: BiometricCredential | undefined,
  now = dayjs().utc()
): { valid: boolean; expired: boolean; credential?: ValidBiometricCredential } => {
  if (
    !credential ||
    !credential.refreshToken ||
    typeof credential.serverDiffMs !== "number" ||
    !credential.refreshExpiresAt
  ) {
    return { valid: false, expired: false };
  }

  const refreshExpiresMoment = dayjs.utc(credential.refreshExpiresAt);
  if (!refreshExpiresMoment.isValid()) {
    return { valid: false, expired: false };
  }

  const currentWithDiff = now.subtract(credential.serverDiffMs, "millisecond");
  const isExpired = refreshExpiresMoment.isBefore(currentWithDiff);

  if (isExpired) {
    return { valid: false, expired: true };
  }

  return {
    valid: true,
    expired: false,
    credential: credential as ValidBiometricCredential,
  };
};

type PersistCredentialParams = {
  username: string;
  refreshToken: string;
  serverDiffMs: number;
  executeAt: string;
  existingExpiresAt?: string | null;
  defaultTtlSeconds?: number;
  state?: string | null;
};

const persistBiometricCredentialInternal = async ({
  username,
  refreshToken,
  serverDiffMs,
  executeAt,
  existingExpiresAt,
  defaultTtlSeconds = BIOMETRIC_REFRESH_TTL_SECONDS,
  state,
}: PersistCredentialParams) => {
  const refreshExpiresAt =
    existingExpiresAt ??
    dayjs.utc(executeAt).add(defaultTtlSeconds, "seconds").toISOString();

  await saveRefreshTokenSecure(username, {
    refreshToken,
    serverDiffMs,
    refreshExpiresAt,
    state,
  });

  return refreshExpiresAt;
};

export const persistRefreshedBiometricCredential = async (
  params: PersistCredentialParams
) => {
  return persistBiometricCredentialInternal({
    ...params,
    defaultTtlSeconds: params.defaultTtlSeconds,
  });
};

export const persistOptInBiometricCredential = async (
  params: Omit<PersistCredentialParams, "defaultTtlSeconds">
) => {
  return persistBiometricCredentialInternal({
    ...params,
    defaultTtlSeconds: BIOMETRIC_REFRESH_TTL_SECONDS,
  });
};

export const clearBiometricCredential = async () => {
  await deleteRefreshTokenSecure();
};
